import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineChat } from 'react-icons/hi';
import axios from 'axios';
import { editorJsToPlainText } from '../utils/editorJsToHtml';
import './estilos/Blog.css';

const Blog = () => {
    const { t, i18n } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const lang = i18n.language === 'es' ? 'es' : 'en';
    const API_URL = process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api';
    const MEDIA_URL = (process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, [selectedCategory, page, lang]);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/blog`, {
                params: {
                    page,
                    limit: 9,
                    category: selectedCategory,
                    lang
                }
            });
            setPosts(response.data.posts);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error('Error al cargar posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_URL}/categories`, {
                params: { lang }
            });
            setCategories(response.data);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        // Tiempo relativo
        if (diffInSeconds < 60) {
            return t('blog.justNow');
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            if (minutes === 1) {
                return t('blog.oneMinuteAgo');
            }
            return lang === 'es' 
                ? `${t('blog.minutesAgo')} ${minutes} ${t('blog.minutes')}`
                : `${minutes} ${t('blog.minutes')}`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            if (hours === 1) {
                return t('blog.oneHourAgo');
            }
            return lang === 'es'
                ? `${t('blog.hoursAgo')} ${hours} ${t('blog.hours')}`
                : `${hours} ${t('blog.hours')}`;
        } else if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            if (days === 1) {
                return t('blog.oneDayAgo');
            }
            return lang === 'es'
                ? `${t('blog.daysAgo')} ${days} ${t('blog.days')}`
                : `${days} ${t('blog.days')}`;
        } else {
            // Fecha completa si es muy antigua
            return date.toLocaleDateString(lang, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }).toUpperCase();
        }
    };

    return (
        <div className="blog-page">
            <div className="blog-header">
                <h1>{t('blog.title', 'Blog')}</h1>
                <p>{t('blog.subtitle', 'Últimas noticias y novedades')}</p>
            </div>

            <div className="blog-container">
                <aside className="blog-sidebar">
                    <div className="sidebar-section">
                        <h3>{t('blog.categories', 'Categorías')}</h3>
                        <ul className="category-list">
                            <li
                                className={!selectedCategory ? 'active' : ''}
                                onClick={() => { setSelectedCategory(null); setPage(1); }}
                            >
                                {t('blog.all', 'Todas')}
                            </li>
                            {categories.map(cat => (
                                <li
                                    key={cat.id}
                                    className={selectedCategory === cat.slug ? 'active' : ''}
                                    onClick={() => { setSelectedCategory(cat.slug); setPage(1); }}
                                >
                                    {cat.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>

                <main className="blog-content">
                    {loading ? (
                        <div className="loading">
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <>
                            <div className="news-list">
                                {posts.map(post => (
                                    <article key={post.id} className="news-card">
                                        <div className="news-content">
                                            <div className="news-header">
                                                {post.category_name && (
                                                    <span className="news-category">
                                                        {post.category_name.toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                            <h2 className="news-title">
                                                <Link to={`/${lang}/blog/${post.slug}`}>
                                                    {post[`title_${lang}`]}
                                                </Link>
                                            </h2>
                                            <p className="news-excerpt">
                                                {post[`excerpt_${lang}`] || editorJsToPlainText(post[`content_${lang}`], 200) || t('blog.clickToReadMore')}...
                                                <Link to={`/${lang}/blog/${post.slug}`} className="read-more-inline">
                                                    {t('blog.readMore', 'LEER MÁS').toUpperCase()} »
                                                </Link>
                                            </p>
                                            <div className="news-meta">
                                                <span className="meta-item meta-comments">
                                                    <HiOutlineChat /> 0
                                                </span>
                                                <span className="meta-divider">-</span>
                                                <span className="meta-item">
                                                    {post.author_name === 'ADMINISTRADOR' 
                                                        ? t('blog.administrator')
                                                        : (post.author_name || 'FENINE')}
                                                </span>
                                                <span className="meta-divider">-</span>
                                                <span className="meta-item">
                                                    {formatDate(post.published_at)}
                                                </span>
                                            </div>
                                        </div>
                                        {post.featured_image && (
                                            <div className="news-image">
                                                <Link to={`/${lang}/blog/${post.slug}`}>
                                                    <img src={`${MEDIA_URL}${post.featured_image}`} alt={post[`title_${lang}`]} />
                                                </Link>
                                            </div>
                                        )}
                                    </article>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="btn-pagination"
                                    >
                                        ← {t('blog.previous', 'Anterior')}
                                    </button>
                                    <span className="page-info">
                                        {t('blog.page', 'Página')} {page} {t('blog.of', 'de')} {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="btn-pagination"
                                    >
                                        {t('blog.next', 'Siguiente')} →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Blog;
